import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Chip,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Assignment,
  Build,
  CheckCircle,
  Dashboard,
  DesktopWindows,
  Description,
  Domain,
  ExpandLess,
  ExpandMore,
  History,
  Inventory2,
  Key,
  Logout,
  Monitor,
  Mouse,
  Notifications,
  People,
  Phone,
  Print,
  Router,
  Security,
} from '@mui/icons-material';

import dadosferaLogo from '../../assets/img/dadosfera-logo.png';
import { useAuth } from '../../context/AuthContext';
import { PHYSICAL_SUBCATEGORIES } from '../../constants/assetConstants';
import { SIDEBAR_WIDTH } from './constants';

const Sidebar = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [assetsOpen, setAssetsOpen] = useState(true);
  const [physicalAssetsOpen, setPhysicalAssetsOpen] = useState(false);

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const canAccess = (requiredRole) => !requiredRole || requiredRole.includes(user?.role);

  const mainItems = [
    {
      text: 'Dashboard',
      description: 'Visão geral do inventário',
      icon: <Dashboard fontSize="small" />,
      path: '/dashboard',
      requiredRole: ['admin', 'manager', 'user'],
    },
    {
      text: 'Homologados',
      description: 'Catálogo de dispositivos homologados',
      icon: <CheckCircle fontSize="small" />,
      path: '/homologated-devices',
      requiredRole: ['admin', 'manager', 'user'],
    },
  ];

  const adminItems = [
    {
      text: 'Gerenciar Usuários',
      description: 'Gestão de acessos e grupos',
      icon: <People fontSize="small" />,
      path: '/users',
      requiredRole: ['admin', 'manager'],
    },
    {
      text: 'Relatórios',
      description: 'Relatórios prontos e personalizados',
      icon: <Description fontSize="small" />,
      path: '/reports',
      requiredRole: ['admin', 'manager'],
    },
    {
      text: 'Alertas',
      description: 'Regras de notificação',
      icon: <Notifications fontSize="small" />,
      path: '/alerts',
      requiredRole: ['admin', 'manager'],
    },
    {
      text: 'Movimentações',
      description: 'Histórico de produtos',
      icon: <History fontSize="small" />,
      path: '/audit',
      requiredRole: ['admin'],
    },
  ];

  const assetCategories = [
    {
      text: 'Físicos',
      icon: <DesktopWindows fontSize="small" />,
      path: '/assets/category/physical',
      description: 'Equipamentos físicos',
      hasSubcategories: true,
    },
    {
      text: 'Tokens',
      icon: <Key fontSize="small" />,
      path: '/assets/category/token',
      description: 'Tokens e credenciais digitais',
    },
    {
      text: 'Licenças',
      icon: <Assignment fontSize="small" />,
      path: '/licenses',
      description: 'Licenças de software',
      dedicated: true,
    },
    {
      text: 'Certificados',
      icon: <Security fontSize="small" />,
      path: '/certificates',
      description: 'Certificados digitais',
      dedicated: true,
    },
    {
      text: 'Domínios',
      icon: <Domain fontSize="small" />,
      path: '/domains',
      description: 'Domínios corporativos',
      dedicated: true,
    },
  ];

  const getSubcategoryIcon = (subcategory) => {
    const map = {
      Computadores: <DesktopWindows fontSize="small" />,
      Monitores: <Monitor fontSize="small" />,
      Impressoras: <Print fontSize="small" />,
      Periféricos: <Mouse fontSize="small" />,
      Telefonia: <Phone fontSize="small" />,
      Servidores: <Router fontSize="small" />,
      Consumíveis: <Inventory2 fontSize="small" />,
      Outro: <Build fontSize="small" />,
    };
    return map[subcategory] || <Inventory2 fontSize="small" />;
  };

  const physicalSubcategories = useMemo(
    () =>
      Object.entries(PHYSICAL_SUBCATEGORIES).map(([key, config]) => ({
        key,
        label: config.label || key,
        description: config.description,
        icon: getSubcategoryIcon(key),
        path: `/assets/category/physical?subcategory=${encodeURIComponent(key)}`,
      })),
    []
  );

  const renderMenuItems = (items, accent = 'primary') =>
    items
      .filter((item) => canAccess(item.requiredRole))
      .map((item) => {
        const isActive = location.pathname === item.path;
        return (
          <Tooltip title={item.description} key={item.text} placement="right">
            <ListItemButton
              selected={isActive}
              onClick={() => handleNavigate(item.path)}
              sx={{
                mx: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: `${accent}.main`,
                  color: `${accent}.contrastText`,
                  '& .MuiListItemIcon-root': {
                    color: `${accent}.contrastText`,
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontWeight: isActive ? 800 : 700, fontSize: '0.9rem' }}
              />
            </ListItemButton>
          </Tooltip>
        );
      });

  const activeSubcategory = new URLSearchParams(location.search).get('subcategory');

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          px: 2,
          py: 2.5,
          backgroundColor: 'primary.main',
          color: 'common.white',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            component="img"
            src={dadosferaLogo}
            alt="Dadosfera"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.22)',
              p: 0.6,
              objectFit: 'contain',
            }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight={800} lineHeight={1}>
              Inventory
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Dadosfera Platform
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Chip
            size="small"
            label={(user?.role || 'user').toUpperCase()}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'common.white',
              fontWeight: 800,
            }}
          />
        </Stack>
      </Box>

      <List sx={{ px: 0, py: 1.25, flex: 1 }}>
        <Typography variant="caption" sx={{ px: 2.5, pb: 0.8, color: 'text.secondary', fontWeight: 800 }}>
          NAVEGAÇÃO
        </Typography>
        {renderMenuItems(mainItems, 'primary')}

        {adminItems.some((item) => canAccess(item.requiredRole)) && (
          <>
            <Typography variant="caption" sx={{ px: 2.5, pt: 1.5, pb: 0.8, color: 'text.secondary', fontWeight: 800 }}>
              GESTÃO
            </Typography>
            {renderMenuItems(adminItems, 'secondary')}
          </>
        )}

        <Divider sx={{ my: 1.2, mx: 1 }} />

        <Typography variant="caption" sx={{ px: 2.5, pb: 0.8, color: 'text.secondary', fontWeight: 800 }}>
          CATÁLOGO DE ATIVOS
        </Typography>

        <ListItemButton onClick={() => setAssetsOpen((prev) => !prev)} sx={{ mx: 1, mb: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Inventory2 fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Ativos"
            primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9rem' }}
          />
          {assetsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </ListItemButton>

        <Collapse in={assetsOpen} unmountOnExit>
          <List disablePadding>
            {assetCategories.map((item) => {
              const isActive = item.dedicated
                ? location.pathname.startsWith(item.path)
                : location.pathname === item.path;

              return (
                <Box key={item.text}>
                  <Tooltip title={item.description} placement="right">
                    <ListItemButton
                      selected={isActive}
                      onClick={() => {
                        if (item.hasSubcategories) {
                          setPhysicalAssetsOpen((prev) => !prev);
                          if (location.pathname !== item.path) {
                            handleNavigate(item.path);
                          }
                          return;
                        }
                        handleNavigate(item.path);
                      }}
                      sx={{ ml: 3, mr: 1, mb: 0.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 34 }}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{ fontSize: '0.86rem', fontWeight: isActive ? 800 : 700 }}
                      />
                      {item.hasSubcategories &&
                        (physicalAssetsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)}
                    </ListItemButton>
                  </Tooltip>

                  {item.hasSubcategories && (
                    <Collapse in={physicalAssetsOpen} unmountOnExit>
                      <List disablePadding>
                        {physicalSubcategories.map((subcategory) => {
                          const isSubActive =
                            location.pathname === '/assets/category/physical' && activeSubcategory === subcategory.key;

                          return (
                            <Tooltip key={subcategory.key} title={subcategory.description || subcategory.label} placement="right">
                              <ListItemButton
                                selected={isSubActive}
                                onClick={() => handleNavigate(subcategory.path)}
                                sx={{ ml: 5, mr: 1, mb: 0.25 }}
                              >
                                <ListItemIcon sx={{ minWidth: 30 }}>{subcategory.icon}</ListItemIcon>
                                <ListItemText
                                  primary={subcategory.label}
                                  primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: isSubActive ? 800 : 700 }}
                                />
                              </ListItemButton>
                            </Tooltip>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </List>
        </Collapse>
      </List>

      <Divider sx={{ mx: 1 }} />

      <Box sx={{ p: 1.25 }}>
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ px: 1, py: 1.2 }}>
          <Avatar sx={{ width: 34, height: 34, fontSize: '0.92rem', fontWeight: 800, bgcolor: 'secondary.main' }}>
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
              {user?.name || 'Usuário'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.email || user?.role || '-'}
            </Typography>
          </Box>
        </Stack>

        <ListItemButton onClick={handleLogout} sx={{ mx: 1 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sair" primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9rem' }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
